/**
 * useAppBadge – aktualisiert plattformübergreifend alle Unread-Indikatoren:
 *
 *  1. PWA-App-Icon (Homescreen / Taskleiste / macOS-Dock) via
 *     `navigator.setAppBadge(count)` (Chrome/Edge ≥ 81, Safari iOS ≥ 16.4 PWA-only).
 *  2. Browser-Tab-Titel (`(3) TeamHub …`).
 *  3. Favicon-Overlay (kleiner roter Kreis mit Zahl) via Canvas-Rendering
 *     für nicht-PWA-Browser-Tabs.
 *
 * Aufruf von zentraler Stelle (z.B. `NotificationBell`) mit dem aktuellen
 * Unread-Count. Wird ein neuer Wert übergeben, werden alle drei Indikatoren
 * synchron aktualisiert; bei 0 wieder geleert.
 */
import { useEffect, useRef } from 'react';

const BASE_TITLE_FALLBACK = 'TeamHub';
const FAVICON_SRC = '/icon.svg';

let cachedBaseTitle = null;

const getBaseTitle = () => {
  if (cachedBaseTitle) return cachedBaseTitle;
  // Beim ersten Aufruf den initialen Titel merken.
  const current = (document.title || BASE_TITLE_FALLBACK).replace(/^\(\d+\)\s*/, '');
  cachedBaseTitle = current || BASE_TITLE_FALLBACK;
  return cachedBaseTitle;
};

const setTabTitle = (count) => {
  const base = getBaseTitle();
  document.title = count > 0 ? `(${count > 99 ? '99+' : count}) ${base}` : base;
};

const setPwaBadge = (count) => {
  try {
    if ('setAppBadge' in navigator) {
      if (count > 0) navigator.setAppBadge(count).catch(() => {});
      else navigator.clearAppBadge().catch(() => {});
    }
  } catch {
    /* setAppBadge ist optional – ignorieren */
  }
};

// --- Favicon-Badge per Canvas -----------------------------------------------
let favImgPromise = null;
const loadFaviconImage = () => {
  if (favImgPromise) return favImgPromise;
  favImgPromise = new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = FAVICON_SRC;
  }).catch(() => null);
  return favImgPromise;
};

const drawFaviconWithBadge = async (count) => {
  const img = await loadFaviconImage();
  if (!img) return null;

  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.clearRect(0, 0, size, size);
  ctx.drawImage(img, 0, 0, size, size);

  if (count > 0) {
    const label = count > 99 ? '99+' : String(count);
    const badgeR = label.length > 2 ? 22 : 18;
    const cx = size - badgeR + 4;
    const cy = badgeR - 4;

    // Roter Kreis mit weißem Rand
    ctx.beginPath();
    ctx.arc(cx, cy, badgeR, 0, Math.PI * 2);
    ctx.fillStyle = '#DC2626';
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#FFFFFF';
    ctx.stroke();

    // Zahl in Weiß
    ctx.fillStyle = '#FFFFFF';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `bold ${label.length > 2 ? 22 : 28}px -apple-system, system-ui, sans-serif`;
    ctx.fillText(label, cx, cy + 1);
  }

  return canvas.toDataURL('image/png');
};

const ensureFaviconLink = () => {
  let link = document.querySelector("link[rel*='icon'][data-app-badge]");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/png';
    link.setAttribute('data-app-badge', '1');
    document.head.appendChild(link);
  }
  return link;
};

const setFavicon = async (count) => {
  const dataUrl = await drawFaviconWithBadge(count);
  if (!dataUrl) return;
  const link = ensureFaviconLink();
  link.href = dataUrl;
};

// --- Hook -------------------------------------------------------------------

export const useAppBadge = (count) => {
  const lastRef = useRef(-1);

  useEffect(() => {
    const c = Math.max(0, Number(count) || 0);
    if (lastRef.current === c) return;
    lastRef.current = c;
    setTabTitle(c);
    setPwaBadge(c);
    setFavicon(c);
  }, [count]);

  useEffect(() => () => {
    // Beim Unmount alles zurücksetzen
    setTabTitle(0);
    setPwaBadge(0);
  }, []);
};

export default useAppBadge;
